/**
 * ClipWave — one clip's waveform, and nothing else.
 *
 * Display only: no drag, no lens, no edge handles. Boundary fixes are made by
 * picking what the clip should CONTAIN (see CutsPage), never by dragging
 * pixels. Tapping the wave pauses/resumes the clip's loop.
 */

import { useCallback, useEffect, useRef } from 'react';

export interface ClipWaveProps {
  /** The whole recording, decoded once by the page. */
  buffer: AudioBuffer;
  startSec: number;
  endSec: number;
  /** Tap: pause/resume the loop. */
  onTap?: () => void;
  className?: string;
}

const H = 96;

export function ClipWave({ buffer, startSec, endSec, onTap, className }: ClipWaveProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const cssW = wrap.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssW * dpr));
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, H);

    const sr = buffer.sampleRate;
    const data = buffer.getChannelData(0);
    const i0 = Math.max(0, Math.floor(startSec * sr));
    const i1 = Math.min(data.length, Math.ceil(endSec * sr));
    const perPx = Math.max(1, Math.floor((i1 - i0) / cssW));

    let peak = 0.001;
    for (let i = i0; i < i1; i += 16) peak = Math.max(peak, Math.abs(data[i]));

    ctx.strokeStyle = getComputedStyle(wrap).getPropertyValue('--wl-wave').trim() || '#8ea0bd';
    for (let x = 0; x < cssW; x++) {
      const s = i0 + x * perPx;
      let min = 0;
      let max = 0;
      for (let i = s; i < Math.min(s + perPx, i1); i++) {
        const v = data[i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      const yTop = H / 2 - (max / peak) * (H / 2 - 4);
      const yBot = H / 2 - (min / peak) * (H / 2 - 4);
      ctx.beginPath();
      ctx.moveTo(x + 0.5, yTop);
      ctx.lineTo(x + 0.5, Math.max(yBot, yTop + 1));
      ctx.stroke();
    }
  }, [buffer, startSec, endSec]);

  useEffect(() => {
    draw();
  }, [draw]);
  useEffect(() => {
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [draw]);

  return (
    <div ref={wrapRef} className={`cw${className ? ` ${className}` : ''}`} onClick={onTap}>
      <canvas ref={canvasRef} className="cw__canvas" style={{ height: H }} />
    </div>
  );
}
